import _, {isUndefined} from 'lodash';
import {DateTime} from 'luxon';
import React, {ReactNode} from 'react';

import {
  build12Or24hOptionsForFormat,
  TimeFormatsEnum,
} from '../../../libs/shared/core/src/helpers/datetime/dateTimeHelpers';
import {isNumberWeekDayNumber} from '../../../libs/shared/core/src/helpers/datetime/luxonHelpers';
import {LocalizeRenderer} from '../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {Localizer} from '../../../libs/shared/core/src/helpers/internationalization/localizeTypes';
import {defineMessage, Plural, Trans} from '../../../libs/shared/core/src/helpers/internationalization/react';
import {DateTimeFormatterBase} from '../../../libs/shared/ui/src/components/datetime/dateTimeFormatterBase';
import {FutureTimeFormatter} from '../../../srcCommon/formatters/futureTimeFormatter';
import {CustomSnoozeModel, RelativeTimeUnitsEnum} from '../../models/api/customSnoozeModel';
import {FutureTimeValuesEnum} from '../../models/api/futureTimeValueModel';
import {
  RelativeTime,
  ReminderPresets,
  ReminderPresetsEnum,
  RReminderPresetsType,
} from '../../models/api/preferencesModel';
import {futureTimeToDate} from '../modelHelpers';

/*
 * Types.
 */

export interface RenderablePreset {
  key: string;
  date: DateTime;
  title: ReactNode;
}

/*
 * Constants.
 */

const weekdayThursday = 4;
const weekdaySunday = 7;

/*
 * Preset Builders.
 */

export function buildAllFutureTimePresets(
  dynamicPresetValues: ReminderPresets | null,
  customSnoozes: ReadonlyArray<CustomSnoozeModel> | null,
  optionalPresetValues: ReadonlyArray<FutureTimeValuesEnum>,
): ReadonlyArray<RenderablePreset> {
  if (customSnoozes !== null) {
    return _(customSnoozes)
      .map((snooze, index) => buildCustomSnoozePreset(snooze, index))
      .compact()
      .value();
  }

  const optionalPresets = buildOptionalPresets(optionalPresetValues);

  // If there are no presets or snoozes, just return the optional presets.
  if (!dynamicPresetValues) {
    return optionalPresets;
  }

  // Otherwise, return the presets and the optional presets.
  const dynamicPresets = _(dynamicPresetValues).map(buildDynamicPreset).compact().value();

  return [...dynamicPresets, ...optionalPresets];
}

function buildCustomSnoozePreset(
  customSnooze: CustomSnoozeModel,
  index: number,
): RenderablePreset | undefined {
  const {relativeTo, offset, name} = customSnooze;

  // Set the current time and calculate the time of the snooze.
  const now = DateTime.local();
  const relativeSnoozeDate = relativeTo && now.startOf(relativeTo).plus(offset);

  // Relative snooze before now: nothing to show.
  if (relativeSnoozeDate && relativeSnoozeDate <= now) {
    return undefined;
  }

  // Relative snooze.
  if (relativeSnoozeDate) {
    return {
      date: relativeSnoozeDate,
      key: index.toString(),
      title: name,
    };
  }

  // Since this is now a relative snooze, compute the absolute date.
  const absoluteSnoozeDate = now.set({
    weekday: isNumberWeekDayNumber(offset.day) ? offset.day : undefined,
    hour: offset.hour,
    minute: offset.minute,
  });

  // Absolute snooze today before now: nothing to show.
  if (isUndefined(offset.day) && absoluteSnoozeDate <= now) {
    return undefined;
  }

  // Absolute snooze today.
  if (isUndefined(offset.day)) {
    return {
      date: absoluteSnoozeDate,
      key: index.toString(),
      title: name,
    };
  }

  // If the snooze is in the past, move a week forward to the next occurence.
  const isSnoozeBeforeNow = absoluteSnoozeDate <= now;
  const futureAbsoluteSnoozeDate = absoluteSnoozeDate.plus({week: isSnoozeBeforeNow ? 1 : 0});

  return {
    date: futureAbsoluteSnoozeDate,
    key: index.toString(),
    title: name,
  };
}

function buildDynamicPreset(
  preset: RelativeTime | null,
  name: string,
  presetValues: ReminderPresets,
): RenderablePreset | null {
  if (!preset) {
    return null;
  }

  const now = DateTime.local();
  const date = now.startOf(preset.relativeTo).plus(preset.offset);

  switch (name) {
    case ReminderPresetsEnum.LATER_TODAY: {
      const thisEveningPreset = presetValues.thisEvening;
      const thisEvening = thisEveningPreset
        ? now.startOf(thisEveningPreset.relativeTo).plus(thisEveningPreset.offset)
        : null;

      // Round to nearest hour.
      const adjustedDate = date.plus({hour: date.minute >= 30 ? 1 : 0}).startOf('hour');

      // Disabled if it is going to be a duplicate of thisEvening.
      if (thisEvening && adjustedDate.equals(thisEvening)) {
        return null;
      }

      return {
        date: adjustedDate,
        key: name,
        title: presetNameRenderers[name],
      };
    }

    case ReminderPresetsEnum.THIS_EVENING: {
      // Disabled if this evening is within a half hour of now.
      if (now.plus({minute: 30}) > date) {
        return null;
      }

      return {
        date,
        key: name,
        title: presetNameRenderers[name],
      };
    }

    case ReminderPresetsEnum.TOMORROW:
      return {
        date,
        key: name,
        title: presetNameRenderers[name],
      };

    case ReminderPresetsEnum.THIS_WEEK: {
      // Disabled Thursday through Saturday
      if (now.weekday >= weekdayThursday && now.weekday < weekdaySunday) {
        return null;
      }

      return {
        date,
        key: name,
        title: presetNameRenderers[name],
      };
    }

    case ReminderPresetsEnum.NEXT_WEEK: {
      // Because weeks begin on Monday, if today is Sunday, add an additional week
      // since the relativeTo set us back to last Monday.
      const adjustedDate = date.plus({week: now.weekday === weekdaySunday ? 1 : 0});

      return {
        date: adjustedDate,
        key: name,
        title: <Trans id="futuretimepresets-nextweek">Next week</Trans>,
      };
    }

    default:
      return null;
  }
}

function buildOptionalPresets(values: ReadonlyArray<FutureTimeValuesEnum>): ReadonlyArray<RenderablePreset> {
  return values.map((futureTime) => ({
    key: futureTime,
    date: DateTime.fromMillis(futureTimeToDate(futureTime)),
    title: <FutureTimeFormatter value={futureTime} />,
  }));
}

const presetNameRenderers: {[k in ReminderPresetsEnum]: ReactNode} = {
  [ReminderPresetsEnum.LATER_TODAY]: <Trans id="futuretimepresets-latertoday">Later today</Trans>,
  [ReminderPresetsEnum.THIS_EVENING]: <Trans id="futuretimepresets-thisevening">This evening</Trans>,
  [ReminderPresetsEnum.TOMORROW]: <Trans id="futuretimepresets-tomorrow">Tomorrow</Trans>,
  [ReminderPresetsEnum.THIS_WEEK]: <Trans id="futuretimepresets-thisweek">This week</Trans>,
  [ReminderPresetsEnum.NEXT_WEEK]: <Trans id="futuretimepresets-nextweek">Next week</Trans>,
};
const presetNameStringRenderers: {[k in ReminderPresetsEnum]: LocalizeRenderer} = {
  [ReminderPresetsEnum.LATER_TODAY]: (localize) =>
    localize(
      defineMessage({
        id: 'futuretimepresets-latertoday',
        message: 'Later today',
      }),
    ),
  [ReminderPresetsEnum.THIS_EVENING]: (localize) =>
    localize(
      defineMessage({
        id: 'futuretimepresets-thisevening',
        message: 'This evening',
      }),
    ),
  [ReminderPresetsEnum.TOMORROW]: (localize) =>
    localize(
      defineMessage({
        id: 'futuretimepresets-tomorrow',
        message: 'Tomorrow',
      }),
    ),
  [ReminderPresetsEnum.THIS_WEEK]: (localize) =>
    localize(
      defineMessage({
        id: 'futuretimepresets-thisweek',
        message: 'This week',
      }),
    ),
  [ReminderPresetsEnum.NEXT_WEEK]: (localize) =>
    localize(
      defineMessage({
        id: 'futuretimepresets-nextweek',
        message: 'Next week',
      }),
    ),
};

export function buildCustomSnoozeFromDynamicPreset(
  preset: RelativeTime | null,
  name: string,
  localize: Localizer,
): CustomSnoozeModel | null {
  if (!RReminderPresetsType.is(name) || !preset) {
    return null;
  }

  // Special case for "next week" default reminder
  if (preset.relativeTo === RelativeTimeUnitsEnum.WEEK) {
    return {
      name: presetNameStringRenderers[name](localize),
      relativeTo: null,
      offset: {
        day: 1,
        hour: preset.offset.hour,
        minute: preset.offset.minute,
      },
    };
  }

  return {
    name: presetNameStringRenderers[name](localize),
    relativeTo: preset.relativeTo,
    offset: preset.offset,
  };
}

export function buildCustomSnoozePresetDescription(snooze: CustomSnoozeModel, timeFormat: TimeFormatsEnum) {
  const {offset, relativeTo} = snooze;

  const now = DateTime.local();
  const date = relativeTo
    ? now.startOf(relativeTo).plus(offset)
    : now.set({
        weekday: isNumberWeekDayNumber(offset.day) ? offset.day : 1,
        hour: offset.hour,
        minute: offset.minute,
      });

  switch (snooze.relativeTo) {
    // Relative To null means adding the day, minute, and hour values immediately.
    case null: {
      const timePart = (
        <DateTimeFormatterBase
          format={{timeStyle: 'short', ...build12Or24hOptionsForFormat(timeFormat)}}
          value={date}
        />
      );
      const weekdayPart = <DateTimeFormatterBase format={{weekday: 'long'}} value={date} />;
      if (!_.isInteger(offset.day)) {
        return <Trans id="snoozepreset-desc-todayat">Today at {timePart}</Trans>;
      }
      if (!_.isInteger(offset.hour)) {
        return <Trans id="snoozepreset-desc-weekdatatsnooze">{weekdayPart} at time of snooze</Trans>;
      }

      return (
        <Trans id="snoozepreset-desc-weekdayat">
          {weekdayPart} at {timePart}
        </Trans>
      );
    }

    // Relative to "day" means picking a time some days/weeks/months away at a specific time.
    case RelativeTimeUnitsEnum.DAY: {
      const timePart = (
        <DateTimeFormatterBase
          format={{timeStyle: 'short', ...build12Or24hOptionsForFormat(timeFormat)}}
          value={date}
        />
      );
      if (offset.month) {
        return (
          <Plural
            id="snooze-presets-relative-time-units-day-month"
            value={offset.month}
            one={<>In 1 month at {timePart}</>}
            other={
              <>
                In {offset.month} months at {timePart}
              </>
            }
          />
        );
      }

      if (offset.week) {
        return (
          <Plural
            id="snooze-presets-relative-time-units-day-week"
            value={offset.week}
            one={<>In 1 week at {timePart}</>}
            other={
              <>
                In {offset.week} weeks at {timePart}
              </>
            }
          />
        );
      }

      if (!_.isInteger(offset.day)) {
        return <Trans id="snoozepreset-desc-todayat">Today at {timePart}</Trans>;
      }

      return (
        <Plural
          id="snooze-presets-relative-time-units-day"
          value={offset.day || 1}
          one={<>In 1 day at {timePart}</>}
          other={
            <>
              In {offset.day} days at {timePart}
            </>
          }
        />
      );
    }

    // Relative to "minute" means picking a time relative to the time of snooze.
    case RelativeTimeUnitsEnum.MINUTE: {
      if (offset.month) {
        return (
          <Plural
            id="snooze-preset-relative-time-units-minute-month"
            value={offset.month}
            one="In 1 month"
            other="In # months"
          />
        );
      }

      if (offset.week) {
        return (
          <Plural
            id="snooze-preset-relative-time-units-minute-week"
            value={offset.week}
            one="In 1 week"
            other="In # weeks"
          />
        );
      }

      if (offset.day) {
        return (
          <Plural
            id="snooze-preset-relative-time-units-dayminute-"
            value={offset.day}
            one="In 1 day"
            other="In # days"
          />
        );
      }

      if (offset.hour) {
        return (
          <Plural
            id="snooze-preset-relative-time-units-minute-hour"
            value={offset.hour}
            one="In 1 hour"
            other="In # hours"
          />
        );
      }

      if (offset.minute) {
        return (
          <Plural
            id="snooze-preset-relative-time-units-minute-minute"
            value={offset.minute}
            one="In 1 minute"
            other="In # minutes"
          />
        );
      }

      return null;
    }

    case RelativeTimeUnitsEnum.WEEK: {
      const weekdayPart = <DateTimeFormatterBase format={{weekday: 'long'}} value={date} />;
      const timePart = (
        <DateTimeFormatterBase
          format={{timeStyle: 'short', ...build12Or24hOptionsForFormat(timeFormat)}}
          value={date}
        />
      );
      return (
        <Trans id="snoozepreset-desc-weekdayat">
          {weekdayPart} at {timePart}
        </Trans>
      );
    }

    default: {
      return null;
    }
  }
}
